<template>
  <ValidationObserver
    ref="addNewMemberObserver"
    v-slot="{ valid, pristine }" >
    <b-modal
      id="add-new-member-modal"
      ref="addNewMemberModal"
      :title="modalTitle"
      modal-class="settings-modal"
      :body-class="['body-wrapper', 'white-bg']"
      hide-header-close
      size="md"
      centered
      @hidden="clear"
      @shown="focus" >

      <ValidationProvider
        v-if="!editMember"
        v-slot="{ errors }"
        ref="emailValidationProviderRef"
        name="Email"
        :rules="!editMember ? 'required' : ''"
        tag="div" >
        <b-form-group>
          <label
            for="text-field"
            class="settings__label mt-0" >
            Email
          </label>
          <multiselect
            v-show="!editMember"
            ref="emailInputRef"
            v-model="emails"
            tag-placeholder=""
            placeholder="Enter emails separated by commas or new lines"
            label="email"
            track-by="id"
            :class="['multiselect-tag-view hide-dropdown', { 'tags-selected': emails.length }]"
            :options="[]"
            :multiple="true"
            :taggable="true"
            :close-on-select="false"
            @tag="addEmail"
            @close="addEmailBlur"
            @search-change="emailSearchChange"
            @remove="removeEmail" >
            <template
              slot="tag"
              slot-scope="{ option, remove }"
              @click.native.stop >
              <span
                :id="`email-pill-${option.id}`"
                aria-hidden="true"
                tabindex="1"
                class="multiselect__tag settings__pill"
                :class="[option.duplicate ? 'settings__pill--yellow shake' : option.valid ? 'settings__pill--blue' : 'settings__pill--red shake']"
                @click="editEmail(option)"
                @mousedown.prevent >
                <span
                  :ref="`email-${option.id}`"
                  contenteditable="plaintext-only"
                  @keydown="catchEnter"
                  @focusout="proccessEmail" >{{ option.email }}</span>
                <SBIcon
                  class="pill__remove"
                  :icon="SBIconList.CLOSE_THICK"
                  :color="option.duplicate ? '#B58E11' : option.valid ? SBColors.bluePrimary : SBColors.redPrimary"
                  :size="6"
                  :hover-size="14"
                  @click.native.stop="remove(option)" />
              </span>
              <b-popover
                v-if="option.duplicate || !option.valid"
                :target="`email-pill-${option.id}`"
                boundary="window"
                triggers="hover"
                placement="top"
                custom-class="basic-popover" >
                {{ option.duplicate ? 'User is already a member' : 'Invalid email' }}
              </b-popover>
            </template>
          </multiselect>
          <p v-if="editMember">{{ editMember.email }}</p>
          <div
            v-if="invalidEmailList || errors[0]"
            class="error flex flex-align-center">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            <span>{{ invalidEmailList ? (invalidEmailList === 1 ? 'Email is invalid.' : `${invalidEmailList} emails are invalid.`) : errors[0] }}</span>
          </div>
        </b-form-group>
      </ValidationProvider>

      <!-- Role selector -->
      <ValidationProvider
        v-slot="{ errors }"
        rules="required"
        tag="div" >
        <b-form-group id="role-group">
          <label
            for="text-field"
            class="settings__label"
            :class="{'mt_0_important': editMember, 'mt-2': !editMember}" >
            Role
          </label>
          <multiselect
            v-model="role"
            placeholder="Select an option"
            class="flex-options hide-tags no-selected-background"
            :options="availableRoles"
            label="name"
            track-by="alias"
            :disabled="isOwner && editMember && me.id === editMember.id"
            :multiple="false"
            :show-labels="false"
            :show-no-results="false"
            :close-on-select="true"
            :clear-on-select="false"
            :preselect-first="true"
            :allow-empty="false"
            :searchable="false" >
            <template
              slot="option"
              slot-scope="props">
              <SBIcon
                :icon="SBIconList.DONE_TICK"
                :color="SBColors.bluePrimary"
                :size="9"/>
              <div class="description-view">
                <h2>{{ props.option.name }}</h2>
                <p>{{ props.option.description }}</p>
              </div>
            </template>
          </multiselect>
          <div
            v-if="errors[0]"
            class="error flex flex-align-center">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            <span>Required field</span>
          </div>
        </b-form-group>
        <b-popover
          v-if="isOwner && editMember && me.id === editMember.id"
          target="role-group"
          triggers="hover"
          placement="bottom"
          custom-class="basic-popover">
          This can't be edited.
        </b-popover>
      </ValidationProvider>

      <!-- Number checkboxes -->
      <div
        v-if="availableNumbers.length"
        style="position: relative; z-index: 0;">
        <b-form-group
          v-if="!availableNumbers.length"
          class="test" >
          <label
            for="text-field"
            class="settings__label flex flex-align-center sticky-top" >
            Numbers
          </label>
          <div
            class="error flex flex-align-center">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            <span>No numbers available on organisation.</span>
          </div>
        </b-form-group>
        <b-form-group v-else>
          <label
            for="text-field"
            class="settings__label flex flex-align-center sticky-top" >
            Numbers
            <!-- && role.alias !== 'admin' -->
            <span
              v-if="selectedNumbers.length"
              class="dot-prefix flex flex-align-center">{{ selectedNumbers.length }}</span>

            <!-- v-if="role.alias !== 'admin'" -->
            <span
              class="greedy checkbox-toggle-wrapper" >
              <span
                class="toggle-all-checkboxes"
                @click="toggleNumbers">{{ selectedNumbers.length === availableNumbers.length ? 'Deselect all' : 'Select all' }}</span>
            </span>
          </label>
          <!-- && role.alias !== 'admin' -->
          <SearchInput
            v-if="showNumberSearch"
            ref="searchInput"
            v-model="searchQuery"
            placeholder="Search numbers..."
            class="no-hover"
            :no-debounce="true"
            :flat="true"
            :icon-size="12"
            :full-width="true"
            @input="filterNumbers" />

          <!-- v-if="role.alias !== 'admin'" -->
          <ValidationProvider
            v-slot="{ errors }"
            rules=""
            tag="div" >
            <b-form-checkbox-group
              v-model="selectedNumbers"
              stacked >
              <b-form-checkbox
                v-for="(business, index) in filteredBusinesses"
                :key="index"
                :value="business.id"
                :class="'checkbox-with-description'" >
                <div class="branch-icon-wrapper">
                  <VNodes :vnodes="getNumberIcon(business)"/>
                </div>
                <div class="option-info">
                  <h3 :inner-html.prop="business.name | boldMatch(searchQuery)" />
                  <p :inner-html.prop="business.phone | phoneFormat | boldMatch(searchQuery)" />
                </div>
              </b-form-checkbox>
            </b-form-checkbox-group>
            <div
              v-if="errors[0] && filteredBusinesses.length !== 0"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>At least one number has to be selected.</span>
            </div>
            <div
              v-if="filteredBusinesses.length === 0"
              class="search-empty flex flex-align-center mt-2">
              <span class="emoji">😐</span>
              <p>We couldn't find anything for <b>{{ searchQuery }}</b></p>
            </div>
          </ValidationProvider>
          <!-- <div
            v-else
            class="flex flex-align-center number-info">
            <SBIcon
              :icon="SBIconList.LOCK_OPEN"
              :color="SBColors.blackPrimary"
              :size="14" />
            <p class="greedy">Admins have a permission to view any number and page, or do any action. </p>
          </div> -->
        </b-form-group>
      </div>

      <template
        slot="modal-footer">
        <b-button
          :disabled="saving"
          @click="hide">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="!!invalidEmailList || saving || !valid"
          :class="{loading: saving}"
          class="flex settings__actions--save"
          @click="submit">
          <span>Save</span>
          <Spinner
            v-if="saving"
            :size="14"
            :thickness="2"
            :color="SBColors.white" />
        </b-button>
      </template>

    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import Multiselect from 'vue-multiselect';
import escapeStringRegexp from 'escape-string-regexp';

import roles from '@/helpers/lists/roles';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import Spinner from '@/components/common/Spinner';
import SearchInput from '@/components/business/widgets/SearchInput';
import { getNumberIcon } from '@/helpers/lists/branchIcons';
import ToasterMixin from '@/mixins/Toaster';
import randomString from '@/helpers/randomString';
import VNodes from '@/components/common/VNodes';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';

export default {
  name: 'InviteTeamMembersModal',
  components: {
    Multiselect,
    SBIcon,
    Spinner,
    SearchInput,
    VNodes,
  },
  mixins: [
    UserPermissionsMixin,
    ToasterMixin,
  ],
  data() {
    return {
      errorMessage: '',
      saving: false,
      emails: [],
      lastEmailSearch: '',
      role: null,
      selectedNumbers: [],
      searchQuery: '',
      editMember: null,
      SBColors,
      SBIconList,
      roles,
      editingEmail: null,
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
      currentOrganisationBusinessIds: 'organisation/currentOrganisationBusinessIds',
      organisationNumbers: 'organisation/organisationNumbers',
      me: 'organisation/me',
      apiError: 'contact/apiError',
    }),
    availableNumbers() {
      if (this.isAdmin || this.isOwner) {
        return this.organisationNumbers;
      }
      return this.currentOrganisation.businesses;
    },
    availableNumbersIds() {
      if (this.isAdmin || this.isOwner) {
        return this.organisationNumbers.map(number => number.id);
      }
      return this.currentOrganisationBusinessIds;
    },
    availableRoles() {
      if (this.editMember && this.isOwner && this.me.id === this.editMember.id) {
        return roles;
      }
      return roles.filter(r => r.alias !== 'owner');
    },
    invalidEmailList() {
      return this.emails.filter(email => !email.valid || email.duplicate).length;
    },
    showNumberSearch() {
      const threshold = 2;
      if (this.availableNumbersIds.length > threshold) {
        return true;
      }
      return false;
    },
    modalTitle() {
      let title = 'Invite team members';
      if (this.editMember) {
        const name = this.editMember.name ? this.editMember.name : this.editMember.email;
        title = `Edit ${name}`;
      }
      return title;
    },
    filteredBusinesses() {
      return this.availableNumbers.filter(business => RegExp(escapeStringRegexp(`${this.searchQuery}`), 'gi').test(business.name) || RegExp(escapeStringRegexp(`${this.searchQuery}`), 'gi').test(business.phone));
    },
  },
  watch: {
    apiError(error) {
      this.saving = false;
      if (error.status === 422) {
        
      } else {
        this.errorMessage = error.data.message;
      }
    },
    role() {
      // if (this.role.alias === 'admin') {
      //   this.selectedNumbers = [...this.currentOrganisationBusinessIds];
      // }
    },
    emails(newValue, oldValue) {
      if (newValue.length < oldValue.length) {
        this.checkCandidatesForDuplicates();
      }
    },
  },
  mounted() {
    this.role = this.availableRoles.at(-1);
  },
  methods: {
    ...mapActions({
      fetchMembers: 'merchant/fetchMembersWithInvitations',
      inviteMembers: 'merchant/inviteMembers',
      editMemberApi: 'merchant/editMember',
      addMemberToBusiness: 'organisation/addMemberToBusiness',
      removeMemberFormBusiness: 'organisation/removeMemberFormBusiness',
    }),
    ...mapMutations({
      updateMemberData: 'merchant/UPDATE_MERCHANT_MEMBERS_INVITATIONS',
    }),
    getNumberIcon,
    addEmail(newEmail) {
      this.parseAndAddEmails(newEmail);
    },
    addEmailBlur() {
      if (this.lastEmailSearch) {
        this.parseAndAddEmails(this.lastEmailSearch);
      }
    },
    focus() {
      if (this.$refs.emailInputRef) {
        this.$refs.emailInputRef.activate();
      }
    },
    parseAndAddEmails(emailsString) {
      // parse out chunks using space or comma
      const parsedEmails = emailsString.split(/[\s,]/g);
      parsedEmails.forEach((email) => {
        if (email) { // skip empty string
          if (this.emails.findIndex(e => e.email.toLowerCase() === email.toLowerCase()) >= 0) {
            return;
          }
          const valid = this.isValidEmail(email);
          this.emails.push({
            email,
            valid,
            id: randomString(),
          });
          this.emails = [...this.emails];
          if (valid) {
            this.isDuplicate(email);
          }
        }
      });
      this.lastEmailSearch = '';
    },
    emailSearchChange(searchQuery) {
      this.lastEmailSearch = searchQuery;
    },
    removeEmail() {},
    // TODO make this generic so it can be applied to all kinds of multiselect tags
    editEmail(emailObject) {
      if (this.editingEmail) {
        // If one email is in edit mode and we select another one than we should blur the previous email first and let it process. Then we can continue with editing the new one.
        this.$refs[`email-${this.editingEmail.id}`].blur();
        setTimeout(() => {
          this.editEmail(emailObject);
        }, 0);
        return;
      }
      this.editingEmail = JSON.parse(JSON.stringify(emailObject));
      this.$refs[`email-${emailObject.id}`].spellcheck = false;
      this.$refs[`email-${emailObject.id}`].focus();
      const selection = document.getSelection();
      const node = this.$refs[`email-${emailObject.id}`].firstChild;

      if (selection.rangeCount) {
        ['Start', 'End'].forEach(pos => selection.getRangeAt(0)[`set${pos}`](node, node.length));
      }
    },
    filterNumbers() {},
    isValidEmail(email) {
      return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
    },
    isDuplicate(email) {
      this.fetchMembers({
        skipVuex: true,
        data: {
          search_query: email,
        },
      }).then((members) => {
        if (members.length && members.some(member => member.email === email)) {
          const duplicateEmails = this.emails.filter(emailItem => emailItem.email === email);
          duplicateEmails.forEach((duplicateEmail) => {
            duplicateEmail.duplicate = true;
          });

          this.emails = [...this.emails];
        }
      });
    },
    getEditingMemberId() {
      let returnId = false;
      if (this.editMember) {
        returnId = this.editMember.id;
      }
      return returnId;
    },
    show(member = null) {
      if (member && member.id) {
        this.editMember = member;
        this.populateForm(member);
      } else {
        this.selectedNumbers = [...this.availableNumbersIds];
      }
      this.$refs.addNewMemberModal.show();
      this.$nextTick(() => { this.$refs.addNewMemberObserver.reset(); });
    },
    resetForm(member) {
      if (member && member.id && !this.saving) {
        this.clear();
        this.editMember = member;
        this.populateForm(member);
      }
    },
    preselectNumbers(numbers) {
      this.$nextTick(() => {
        if (Array.isArray(numbers)) {
          numbers.forEach((numberId) => {
            if (!this.selectedNumbers.includes(numberId)) {
              this.selectedNumbers.push(numberId);
            }
          });
        } else {
          this.selectedNumbers.push(numbers);
        }
      });
    },
    populateForm(member) {
      this.emails.push({ email: member.email, valid: true });
      this.role = this.availableRoles.find(r => r.alias === member.role);
      this.selectedNumbers = member.businesses.map(business => business.uid);
    },
    submit(e) {
      e.preventDefault();
      this.saving = true;
      const payload = {
        data: {
          email: this.emails.map(emailObject => emailObject.email),
          // business_uid: this.role.alias === 'admin' ? this.currentOrganisationBusinessIds : this.selectedNumbers,
          business_uid: this.selectedNumbers,
          role: this.role.alias,
        },
      };

      if (this.editMember) {
        this.patchMember(payload);
      } else {
        this.sendInvite(payload);
      }
    },
    patchMember(payload) {
      const name = this.editMember.name ? this.editMember.name : payload.data.email;
      delete payload.data.email;
      payload.memberId = this.editMember.id;
      this.editMemberApi(payload)
        .then((response) => {
          if (response.status === 200) {
            const businessesToSet = this.availableNumbers
              .filter(business => payload.data.business_uid.includes(business.id))
              .map(business => (
                {
                  name: business.name,
                  uid: business.id,
                }
              ));

            if (this.me.id === this.editMember.id) {
              const diffToAdd = businessesToSet.filter(b => !this.currentOrganisationBusinessIds.includes(b.uid)).map(b => b.uid);
              const businessesToSetIds = businessesToSet.map(b => b.uid);
              const diffToRemove = this.currentOrganisationBusinessIds.filter(b => !businessesToSetIds.includes(b));

              diffToAdd.forEach(item => this.addMemberToBusiness({
                business_uid: item,
                user_uid: this.me.id,
              }));

              diffToRemove.forEach(item => this.removeMemberFormBusiness({
                business_uid: item,
                user_uid: this.me.id,
              }));
            }

            this.updateMemberData({
              targetMemberId: this.editMember.id,
              newData: {
                businesses: businessesToSet,
                role: payload.data.role,
              },
            });
            this.showToaster(`${name} has been successfully updated.`, null, null, 'OK');
            this.hide();
          }
        })
        .catch(() => {
          this.showToaster(`There was an error while updating member ${name}. Please try again.`, null, null, 'error');
          this.saving = false;
        });
    },
    catchEnter(e) {
      if (e.keyCode === 13) {
        e.preventDefault();
        this.$refs[`email-${this.editingEmail.id}`].blur();
      }
    },
    proccessEmail(e) {
      if (!this.editingEmail) {
        return;
      }
      const newValue = e.target.innerText;

      const valid = this.isValidEmail(newValue);
      const targetEmail = this.emails.find(email => email.id === this.editingEmail.id);

      if (targetEmail) {
        if (
          newValue !== this.editingEmail.email &&
          this.emails.findIndex(email => email.email.toLowerCase() === newValue.toLowerCase()) >= 0) {
          targetEmail.duplicate = true;
        } else {
          targetEmail.duplicate = false;
        }
        targetEmail.email = newValue;
        targetEmail.valid = valid;
      }

      if (valid) {
        this.isDuplicate(newValue);
      }

      this.editingEmail = null;
    },
    checkCandidatesForDuplicates() {
      this.emails.forEach((email) => {
        const counter = this.emails.filter(e => e.email === email.email).length;
        if (counter === 1) {
          email.duplicate = false;
        }
      });
    },
    sendInvite(payload) {
      this.inviteMembers(payload)
        .then((response) => {
          if (response.status === 200) {
            this.$emit('invite-sent');
            if (payload.data.email.length === 1) {
              this.showToaster(`${payload.data.email[0]} has been successfully invited.`, null, null, 'OK');
            } else {
              this.showToaster('Members have been successfully invited.', null, null, 'OK');
            }

            this.hide();
          }
        })
        .catch(() => {
          if (payload.data.email.length === 1) {
            this.showToaster(`There was an error while sending an invitation to ${payload.data.email[0]}. Please try again.`, null, null, 'error');
          } else {
            this.showToaster('There was an error while sending invitations to new members. Please try again.', null, null, 'error');
          }
          this.saving = false;
        });
    },
    toggleNumbers() {
      if (this.selectedNumbers.length === this.availableNumbersIds.length) {
        this.selectedNumbers = [];
      } else {
        this.selectedNumbers = [...this.availableNumbersIds];
      }
    },
    clear() {
      this.saving = false;
      this.editMember = null;
      this.errorMessage = '';
      this.emails = [];
      this.lastEmailSearch = '';
      this.role = this.availableRoles.at(-1);
      this.selectedNumbers = [];
      this.searchQuery = '';
    },
    hide() {
      this.$refs.addNewMemberModal.hide();
    },
  },
};
</script>


<style lang="scss">
#add-new-member-modal {
  // max-height: 76% !important;
  // top: 12%;

  .modal-body {
    padding-top: 0 !important;

    & > div:first-child {
      margin-top: 20px;
    }

    .mt_0_important {
      margin-top: 0 !important;
    }
  }

  .branch-icon-wrapper {
    .branch-icon {
      &.FRONTAPP,
      &.FRONTPACHA {
        background-image: url('~@/assets/img/SB-Icons/integration-front-color.svg');
        background-size: 14px 14px;
        background-position: center center;
        background-repeat: no-repeat;
        display: block;
        width: 31px;
        height: 31px;
        transform: translateY(0);
      }
    }
  }

  .number-info {
    padding: 15px;
    border-radius: 5px;
    border: solid 1px #D9E2EA;
    background-color: #F0F8FF;

    .sb-icon {
      margin-right: 11px;
    }
  }

  .sticky-top {
    margin: 15px 0 0 0 !important;
    padding: 5px 0 !important;
    background-color: #f8fafb !important;
    z-index: 49 !important;
  }

  .flat {
    margin-left: 6px !important;

    .search-wrapper input {
      padding-left: 27px !important;
    }
  }

  .search-empty {
    color: $blackLight;

    .emoji {
      margin-left: 6px !important;
      font-size: 13px !important;
      width: 20px !important;
    }

    p {
      margin-left: 7px;

      b {
        font-weight: 500 !important;
      }
    }
  }

  .checkbox-with-description {
    .custom-control-label {
      padding-left: 0px !important;
    }
  }

  .no-hover input {
    box-shadow: none !important;
  }
}
</style>
